import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, catchError } from 'rxjs';
import { ConfigService } from './config.service';

/**
 * Headers are managed by HTTP interceptors (see core/interceptors/)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  private get baseUrl(): string {
    return this.configService.api.host;
  }

  get<T>(endpoint: string, params?: Record<string, unknown>): Observable<T> {
    let httpParams = new HttpParams();

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        httpParams = this.appendQueryParam(httpParams, key, value);
      });
    }

    return this.http.get<T>(`${this.baseUrl}${endpoint}`, {
      params: httpParams,
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  private appendQueryParam(params: HttpParams, key: string, value: unknown): HttpParams {
    if (value === null || value === undefined || value === '') {
      return params;
    }

    let result = params;

    if (Array.isArray(value)) {
      if (
        value.length > 0 &&
        typeof value[0] === 'object' &&
        value[0] !== null &&
        !Array.isArray(value[0])
      ) {
        value.forEach((item, index) => {
          Object.entries(item as Record<string, unknown>).forEach(([itemKey, itemValue]) => {
            result = this.appendQueryParam(
              result,
              `${key}[${index}][${itemKey}]`,
              itemValue
            );
          });
        });

        return result;
      }

      value.forEach((item, index) => {
        result = this.appendQueryParam(result, `${key}[${index}]`, item);
      });

      return result;
    }

    return params.set(key, String(value));
  }

  post<T>(endpoint: string, data?: unknown): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}${endpoint}`, data)
      .pipe(
        catchError(this.handleError)
      );
  }

  put<T>(endpoint: string, data?: unknown): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}${endpoint}`, data)
      .pipe(
        catchError(this.handleError)
      );
  }
  patch<T>(endpoint: string, data?: unknown): Observable<T> {
    return this.http.patch<T>(`${this.baseUrl}${endpoint}`, data)
      .pipe(
        catchError(this.handleError)
      );
  }

  delete<T>(endpoint: string): Observable<T> {
    return this.http.delete<T>(`${this.baseUrl}${endpoint}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      errorMessage = error.error?.message || error.error?.error?.message || `Server Error: ${error.status}`;
    }

    console.error('HTTP Error:', {
      status: error.status,
      message: errorMessage,
      url: error.url,
      error: error.error
    });

    return throwError(() => ({
      status: error.status,
      data: error.error,
      message: errorMessage
    }));
  };
}
